'use client';
import React, { useState } from 'react';

const latestPosts = [
  {
    category: 'PHYSICS',
    date: 'Jun 17, 2025',
    title: 'String Theory at 50: Has It Delivered?',
    excerpt: 'Five decades after its inception, string theory remains the most mathematically beautiful and empirically unverified framework in physics.',
    tag: 'THEORY',
  },
  {
    category: 'BIOLOGY',
    date: 'Jun 16, 2025',
    title: 'Horizontal Gene Transfer: Evolution\'s Secret Shortcut',
    excerpt: 'Genes don\'t just pass from parent to child. Bacteria swap DNA laterally, rewriting the rules of inheritance and evolution.',
    tag: 'SCIENCE',
  },
  {
    category: 'PHILOSOPHY',
    date: 'Jun 14, 2025',
    title: 'The Ship of Theseus and the Problem of Identity',
    excerpt: 'If every plank is replaced, is it still the same ship? This ancient paradox cuts to the heart of what it means for anything to persist through time.',
    tag: 'OBSERVATION',
  },
  {
    category: 'COSMOLOGY',
    date: 'Jun 13, 2025',
    title: 'The Multiverse: Infinite Beginnings',
    excerpt: 'If our universe had an inception, what stopped it from happening an infinite number of times? The multiverse hypothesis suggests it did.',
    tag: 'THEORY',
  },
  {
    category: 'ANTHROPOLOGY',
    date: 'Jun 11, 2025',
    title: 'Fire: The Technology That Made Us Human',
    excerpt: 'Cooking with fire didn\'t just change our diet — it changed our brains, our social structures, and the entire trajectory of human evolution.',
    tag: 'HISTORY',
  },
  {
    category: 'MATHEMATICS',
    date: 'Jun 10, 2025',
    title: 'Zero: The Number That Changed Everything',
    excerpt: 'The concept of zero was invented independently in multiple civilizations. Its inception unlocked algebra, calculus, and modern computing.',
    tag: 'STORY',
  },
  {
    category: 'GEOLOGY',
    date: 'Jun 9, 2025',
    title: 'Plate Tectonics: Earth\'s Slow-Motion Revolution',
    excerpt: 'The continents were once a single landmass. The forces that tore them apart also built every mountain range, ocean trench, and volcanic island.',
    tag: 'SCIENCE',
  },
  {
    category: 'NEUROSCIENCE',
    date: 'Jun 8, 2025',
    title: 'The Default Mode Network: Your Brain\'s Idle State',
    excerpt: 'When you\'re doing nothing, your brain is most active. The default mode network is where self-reflection, creativity, and daydreaming are born.',
    tag: 'OBSERVATION',
  },
  {
    category: 'CHEMISTRY',
    date: 'Jun 7, 2025',
    title: 'Water: The Molecule That Defies Its Own Rules',
    excerpt: 'Water is the most anomalous substance on Earth. Its strange properties — ice floating, high surface tension — are precisely what makes life possible.',
    tag: 'SCIENCE',
  },
  {
    category: 'ASTRONOMY',
    date: 'Jun 6, 2025',
    title: 'Neutron Stars: The Densest Objects That Aren\'t Black Holes',
    excerpt: 'A teaspoon of neutron star material weighs a billion tons. These stellar remnants are the universe\'s most extreme laboratories.',
    tag: 'MYSTERY',
  },
  {
    category: 'EVOLUTION',
    date: 'Jun 5, 2025',
    title: 'The Cambrian Explosion: Life\'s Big Bang',
    excerpt: 'In just 20 million years, nearly every major animal body plan appeared. The Cambrian Explosion is evolution\'s most dramatic inception event.',
    tag: 'HISTORY',
  },
  {
    category: 'TECHNOLOGY',
    date: 'Jun 4, 2025',
    title: 'The Transistor: The Invention That Runs the World',
    excerpt: 'Invented in 1947, the transistor is the most manufactured object in human history. Every digital device you own runs on its principles.',
    tag: 'STORY',
  },
];

const categories = [
  'All Topics',
  'Cosmology',
  'Biology',
  'Physics',
  'Philosophy',
  'Chemistry',
  'Civilization',
  'Mathematics',
  'Evolution',
  'Technology',
  'Neuroscience',
  'Astronomy',
  'Geology',
];

const contentTypes = [
  { label: 'Articles', checked: false },
  { label: 'Theories', checked: false },
  { label: 'Stories', checked: false },
  { label: 'Observations', checked: false },
  { label: 'Videos', checked: false },
  { label: 'Deep Dives', checked: false },
];

export default function ResearchFeed() {
  const [activeCategory, setActiveCategory] = React.useState('All Topics');
  const [checkedTypes, setCheckedTypes] = React.useState<Record<string, boolean>>({});
  const [sortBy, setSortBy] = React.useState('Date');
  const [searchVal, setSearchVal] = React.useState('');
  const [visibleCount, setVisibleCount] = React.useState(8);

  const filteredPosts = latestPosts.filter((post) => {
    const matchCat =
      activeCategory === 'All Topics' ||
      post.category.toLowerCase() === activeCategory.toLowerCase();
    const anyTypeChecked = Object.values(checkedTypes).some(Boolean);
    const matchType = !anyTypeChecked || checkedTypes[post.tag];
    const matchSearch =
      !searchVal ||
      post.title.toLowerCase().includes(searchVal.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchVal.toLowerCase());
    return matchCat && matchType && matchSearch;
  });

  const sorted = [...filteredPosts].sort((a, b) => {
    if (sortBy === 'Popularity') return 0;
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  const visible = sorted.slice(0, visibleCount);

  return (
    <section id="theories" className="bg-[#070a14] py-10 border-b border-[#1e2a3a]">
      <div className="max-w-[1280px] mx-auto px-6 md:px-16">
        <h2
          className="text-white font-semibold mb-6"
          style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '25.6px', fontWeight: 500 }}
        >
          Explore the Feed
        </h2>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <div className="lg:w-64 flex-shrink-0">
            {/* Category Filter */}
            <div className="bg-[#0d1120] border border-[#1e2a3a] rounded-sm p-5 mb-4">
              <h5
                className="text-white font-semibold text-[13px] mb-3 uppercase tracking-wider"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Filter By Topic
              </h5>
              <ul className="space-y-1.5">
                {categories.map((cat) => (
                  <li key={cat}>
                    <label className="flex items-center gap-2.5 cursor-pointer group">
                      <input
                        type="radio"
                        name="category"
                        checked={activeCategory === cat}
                        onChange={() => { setActiveCategory(cat); setVisibleCount(8); }}
                        className="accent-[#e01a2e] w-3.5 h-3.5"
                      />
                      <span
                        className={`text-[13px] transition-colors duration-150 ${
                          activeCategory === cat ? 'text-[#e01a2e]' : 'text-[#a0aec0] group-hover:text-white'
                        }`}
                        style={{ fontFamily: 'Roboto, sans-serif' }}
                      >
                        {cat}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>

            {/* Content Type Filter */}
            <div className="bg-[#0d1120] border border-[#1e2a3a] rounded-sm p-5 mb-4">
              <h5
                className="text-white font-semibold text-[13px] mb-3 uppercase tracking-wider"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Content Type
              </h5>
              <ul className="space-y-1.5">
                {contentTypes.map((type) => (
                  <li key={type.label}>
                    <label className="flex items-center gap-2.5 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={!!checkedTypes[type.label.toUpperCase()]}
                        onChange={(e) =>
                          setCheckedTypes((prev) => ({
                            ...prev,
                            [type.label.toUpperCase()]: e.target.checked,
                          }))
                        }
                        className="accent-[#e01a2e] w-3.5 h-3.5"
                      />
                      <span
                        className="text-[13px] text-[#a0aec0] group-hover:text-white transition-colors duration-150"
                        style={{ fontFamily: 'Roboto, sans-serif' }}
                      >
                        {type.label}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>

            {/* Sort + Search */}
            <div className="bg-[#0d1120] border border-[#1e2a3a] rounded-sm p-5">
              <h5
                className="text-white font-semibold text-[13px] mb-2 uppercase tracking-wider"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Sort By
              </h5>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full bg-[#111827] border border-[#2d3748] text-[#a0aec0] text-[13px] px-3 py-2 rounded-sm mb-4 outline-none focus:border-[#e01a2e] transition-colors"
                style={{ fontFamily: 'Roboto, sans-serif' }}
              >
                <option>Date</option>
                <option>Popularity</option>
              </select>
              <h5
                className="text-white font-semibold text-[13px] mb-2 uppercase tracking-wider"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Search
              </h5>
              <div className="relative">
                <input
                  type="text"
                  value={searchVal}
                  onChange={(e) => { setSearchVal(e.target.value); setVisibleCount(8); }}
                  placeholder="Search articles..."
                  className="w-full bg-[#111827] border border-[#2d3748] text-white text-[13px] px-3 py-2 rounded-sm outline-none focus:border-[#e01a2e] transition-colors placeholder-[#4a5568]"
                  style={{ fontFamily: 'Roboto, sans-serif' }}
                />
              </div>
            </div>
          </div>

          {/* Feed Grid */}
          <div className="flex-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {visible.map((post, i) => (
                <FeedCard key={i} post={post} />
              ))}
            </div>

            {visible.length === 0 && (
              <div className="text-center py-16">
                <p className="text-[#4a5568] text-[15px]" style={{ fontFamily: 'Roboto, sans-serif' }}>
                  No articles found matching your filters.
                </p>
              </div>
            )}

            {visibleCount < sorted.length && (
              <div className="mt-8 text-center">
                <button
                  onClick={() => setVisibleCount((v) => v + 4)}
                  className="inline-flex items-center gap-2 border border-[#2d3748] hover:border-[#e01a2e] text-[#a0aec0] hover:text-white text-[13px] font-semibold px-6 py-3 rounded-[3px] transition-all duration-200"
                  style={{ fontFamily: 'Roboto, sans-serif' }}
                >
                  Load More Articles
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function FeedCard({ post }: { post: typeof latestPosts[0] }) {
  return (
    <div className="group cursor-pointer bg-[#0d1120] border border-[#1e2a3a] hover:border-[#e01a2e] rounded-sm p-4 transition-all duration-300 hover:shadow-[0_4px_20px_rgba(224,26,46,0.1)]">
      <div className="flex items-center gap-2 mb-2">
        <span
          className="text-[9px] font-bold tracking-widest text-[#e01a2e] uppercase"
          style={{ fontFamily: 'Roboto, sans-serif' }}
        >
          {post.category}
        </span>
        <span className="text-[#2d3748]">·</span>
        <span className="text-[#4a5568] text-[11px]" style={{ fontFamily: 'Roboto, sans-serif' }}>
          {post.date}
        </span>
      </div>
      <h3
        className="text-white font-bold text-[14px] leading-snug mb-2 group-hover:text-[#e01a2e] transition-colors duration-200"
        style={{ fontFamily: 'Montserrat, sans-serif' }}
      >
        {post.title}
      </h3>
      <p
        className="text-[#a0aec0] text-[12px] leading-relaxed line-clamp-2 mb-3"
        style={{ fontFamily: 'Roboto, sans-serif' }}
      >
        {post.excerpt}
      </p>
      <div className="flex items-center justify-between">
        <span
          className="inline-block text-[9px] font-bold tracking-widest px-2 py-0.5 rounded-sm"
          style={{
            fontFamily: 'Roboto, sans-serif',
            background: 'rgba(224,26,46,0.12)',
            color: '#e01a2e',
            border: '1px solid rgba(224,26,46,0.2)',
          }}
        >
          {post.tag}
        </span>
        <span className="text-[#e01a2e] text-[11px] font-semibold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          Read more
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </span>
      </div>
    </div>
  );
}
